/**
 * Mobile Live Draft
 * Clean, high-density draft interface for mobile
 * Tab-based: Board | Players | My Roster
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import { dynastyTheme, withAlpha } from '../../../services/colorService';

import { leaguesAPI, authAPI } from '../../../services/apiService';

import { MobileDynastyTable, renderFloat2 } from '../components/MobileDynastyTable';
import MobilePositionAssignmentModal from '../components/MobilePositionAssignmentModal';
import MobilePlayerLink from '../components/MobilePlayerLink';
import { ChevronDown, ChevronUp, Wifi, WifiOff, Clock, AlertCircle } from 'lucide-react';
import { isPitcherPosition } from '../../../utils/playerClassification';

const C = dynastyTheme.tokens.colors;

const getStoredToken = () => {
  try {
    return localStorage.getItem('fantasy_auth_token') || sessionStorage.getItem('fantasy_auth_token');
  } catch (e) {
    return null;
  }
};

const MobileLiveDraft = ({ leagueId: propLeagueId }) => {
  const params = useParams();
  const navigate = useNavigate();
  const leagueId = propLeagueId || params.leagueId;

  // Core state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [connected, setConnected] = useState(false);
  const [draftState, setDraftState] = useState(null);
  const [league, setLeague] = useState(null);
  const [userTeam, setUserTeam] = useState(null);
  const [teamsMap, setTeamsMap] = useState(new Map());

  // UI state
  const [activeTab, setActiveTab] = useState('players'); // 'board' | 'players' | 'roster'
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [showPositionModal, setShowPositionModal] = useState(false);
  const [makingPick, setMakingPick] = useState(false);
  const [timerData, setTimerData] = useState(null);
  const [filters, setFilters] = useState({ position: 'ALL', search: '' });

  // Roster sections
  const [expandedSections, setExpandedSections] = useState({
    hitting: true,
    bench: false,
    minors: false
  });

  // Available players
  const [availablePlayers, setAvailablePlayers] = useState([]);
  const [loadingPlayers, setLoadingPlayers] = useState(false);

  const socketRef = useRef(null);

  // Derived: Is it user's turn?
  const isYourTurn = useMemo(() => {
    return draftState?.currentPick?.current_owner_team_id === userTeam?.team_id;
  }, [draftState?.currentPick, userTeam?.team_id]);

  // Derived: Drafted player IDs
  const draftedPlayerIds = useMemo(() => {
    return draftState?.picks?.filter(p => p.league_player_id).map(p => p.league_player_id) || [];
  }, [draftState?.picks]);

  // Connect on mount
  useEffect(() => {
    connectToDraft();
    return () => {
      if (socketRef.current) socketRef.current.disconnect();
    };
  }, [leagueId]);

  // Load available players
  useEffect(() => {
    if (leagueId && league) loadAvailablePlayers();
  }, [leagueId, league]);

  // Keep-alive auth refresh
  useEffect(() => {
    const interval = setInterval(async () => {
      try { await authAPI.checkAuth(); } catch (e) {}
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const connectToDraft = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await leaguesAPI.getLiveDraftConnection(leagueId);
      if (!response.success || !response.live_draft_enabled) {
        throw new Error(response.message || 'Live draft not enabled');
      }

      setUserTeam(response.user_team);

      const leagueResponse = await leaguesAPI.getLeagueDetails(leagueId);
      if (leagueResponse.success) setLeague(leagueResponse.league);

      const draftYear = leagueResponse.league?.draft_year || leagueResponse.league?.current_season;
      if (!draftYear) throw new Error('Could not determine draft year');

      // Load teams
      let teamsMapLocal = new Map();
      try {
        const teamsResponse = await leaguesAPI.getLeagueTeams(leagueId);
        if (teamsResponse.success && teamsResponse.teams) {
          teamsResponse.teams.forEach(team => {
            teamsMapLocal.set(team.team_id, { team_id: team.team_id, team_name: team.team_name });
          });
          setTeamsMap(teamsMapLocal);
        }
      } catch (e) {}

      const accessToken = getStoredToken();
      if (!accessToken) throw new Error('Authentication token not found');

      const socket = io(response.websocket_url, {
        auth: { token: accessToken },
        transports: ['polling', 'websocket'],
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionAttempts: 5,
        timeout: 20000
      });

      socketRef.current = socket;

      socket.on('connect', () => {
        setConnected(true);
        socket.emit('join-draft', { league_id: leagueId, year: draftYear });
      });

      socket.on('disconnect', () => setConnected(false));
      socket.on('connect_error', (err) => {
        setError(err.message);
        setLoading(false);
      });

      socket.on('draft-state', async (state) => {
        // Enrich picks with team names
        if (state.picks) {
          state.picks = state.picks.map(pick => {
            const teamId = pick.current_owner_team_id || pick.current_owner?.team_id;
            if (teamId) {
              const team = teamsMapLocal.get(teamId);
              if (team) pick.current_owner = team;
            }
            return pick;
          });
        }
        if (state.currentPick?.current_owner_team_id && !state.currentPick.current_owner) {
          const team = teamsMapLocal.get(state.currentPick.current_owner_team_id);
          if (team) state.currentPick.current_owner = team;
        }
        setDraftState(state);
        setLoading(false);
      });

      socket.on('timer-start', (data) => setTimerData(data));
      socket.on('timer-expired', () => setTimerData(null));
      socket.on('error', (data) => setError(data.message));

    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const loadAvailablePlayers = async () => {
    try {
      setLoadingPlayers(true);
      const response = await leaguesAPI.getAvailablePlayersForDraft(leagueId, {
        limit: 500,
        sort_by: 'baseline_price',
        sort_order: 'desc'
      });
      if (response.success) {
        setAvailablePlayers(response.players || response.data?.players || []);
      }
    } catch (e) {
      console.error('Failed to load players:', e);
    } finally {
      setLoadingPlayers(false);
    }
  };

  // Filter available players
  const filteredPlayers = useMemo(() => {
    let players = availablePlayers.filter(p => !draftedPlayerIds.includes(p.ids?.league_player));

    if (filters.position !== 'ALL') {
      players = players.filter(p => {
        const pos = p.info?.position;
        if (filters.position === 'BATTERS') return !isPitcherPosition(pos);
        if (filters.position === 'PITCHERS') return isPitcherPosition(pos);
        return pos === filters.position;
      });
    }

    if (filters.search) {
      const search = filters.search.toLowerCase();
      players = players.filter(p => {
        const name = (p.info?.full_name || '').toLowerCase();
        return name.includes(search);
      });
    }

    return players;
  }, [availablePlayers, draftedPlayerIds, filters]);

  // User's drafted players
  const userDraftedPlayers = useMemo(() => {
    if (!draftState?.picks || !userTeam?.team_id) return [];
    return draftState.picks.filter(p =>
      p.current_owner_team_id === userTeam.team_id && p.league_player_id
    );
  }, [draftState?.picks, userTeam?.team_id]);

  // Transform for position assignment
  const playerForAssignment = useMemo(() => {
    if (!selectedPlayer) return null;
    const pos = selectedPlayer.info?.position;
    const ELIGIBILITY = {
      "C": ["C", "UTIL"], "1B": ["1B", "CI", "UTIL"], "2B": ["2B", "MI", "UTIL"],
      "3B": ["3B", "CI", "UTIL"], "SS": ["SS", "MI", "UTIL"],
      "LF": ["OF", "UTIL"], "CF": ["OF", "UTIL"], "RF": ["OF", "UTIL"], "OF": ["OF", "UTIL"],
      "DH": ["UTIL"], "SP": ["P"], "RP": ["P"], "P": ["P"]
    };
    const elig = ELIGIBILITY[pos] || [];
    return {
      ...selectedPlayer,
      info: {
        first_name: selectedPlayer.info?.first_name,
        last_name: selectedPlayer.info?.last_name,
        full_name: selectedPlayer.info?.full_name,
        position: pos,
        mlb_team: selectedPlayer.info?.mlb_team
      },
      league_context: {
        roster: { status: null, position: null, effective_eligibility: elig, default_eligibility: elig }
      }
    };
  }, [selectedPlayer]);

  // Roster for assignment modal
  const userRosterForAssignment = useMemo(() => {
    return userDraftedPlayers.map(pick => ({
      ids: { league_player: pick.league_player_id },
      info: { full_name: pick.player?.info?.full_name, position: pick.player?.info?.position },
      league_context: { roster: { status: pick.roster_status || 'active', position: pick.roster_position } }
    }));
  }, [userDraftedPlayers]);

  // Handle position assignment
  const handlePositionAssignment = async (data) => {
    if (!selectedPlayer || makingPick || !draftState?.currentPick) return;
    setMakingPick(true);
    setShowPositionModal(false);
    try {
      socketRef.current.emit('make-pick', {
        pick_id: draftState.currentPick.pick_id,
        league_player_id: selectedPlayer.ids?.league_player,
        contract_years: 1,
        roster_status: data.roster_status,
        roster_position: data.roster_position
      });
      setSelectedPlayer(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setMakingPick(false);
    }
  };

  // Toggle section
  const toggleSection = (key) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Columns for available players table
  const playerColumns = useMemo(() => [
    {
      key: 'player_name',
      header: 'Player',
      width: '120px',
      sticky: true,
      textAlign: 'left',
      accessor: (row) => row.info?.full_name,
      render: (value, row) => {
        const player = row;
        const pos = player?.info?.position;
        const team = player?.info?.mlb_team || '';
        const isSelected = selectedPlayer?.ids?.league_player === player?.ids?.league_player;
        return (
          <div
            onClick={() => setSelectedPlayer(player)}
            style={{
              cursor: 'pointer',
              padding: '2px 4px',
              borderRadius: '3px',
              backgroundColor: isSelected ? withAlpha(C.goldBase, 0.2) : 'transparent',
              border: isSelected ? `1px solid ${withAlpha(C.goldBase, 0.5)}` : '1px solid transparent'
            }}
          >
            <MobilePlayerLink
              mlbId={player?.ids?.mlb}
              name={value}
              onClick={() => setSelectedPlayer(player)}
              style={{ fontWeight: 600, fontSize: 11 }}
            />
            <div style={{ fontSize: '9px', color: dynastyTheme.tokens.colors.neutral[400] }}>
              {pos} • {team}
            </div>
          </div>
        );
      }
    },
    {
      key: 'price',
      header: '$',
      width: '40px',
      textAlign: 'right',
      accessor: (row) => row.financial?.baseline_price || 0,
      render: (value) => (
        <span style={{ color: dynastyTheme.tokens.colors.success, fontWeight: '600', fontSize: '10px' }}>
          {value ? `$${value.toFixed(0)}` : '-'}
        </span>
      )
    }
  ], [selectedPlayer]);

  // Render loading
  if (loading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: '32px', height: '32px', border: '3px solid transparent', borderTopColor: dynastyTheme.tokens.colors.primary, borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 12px' }} />
          <div style={{ color: C.white, fontSize: '13px' }}>Connecting to draft...</div>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // Render error
  if (error && !draftState) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: 'transparent', padding: '20px' }}>
        <div style={{ backgroundColor: dynastyTheme.tokens.colors.neutral[800], borderRadius: '8px', padding: '16px', border: `1px solid ${dynastyTheme.tokens.colors.error}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <AlertCircle size={18} color={dynastyTheme.tokens.colors.error} />
            <span style={{ color: dynastyTheme.tokens.colors.error, fontWeight: '600', fontSize: '13px' }}>Connection Error</span>
          </div>
          <div style={{ color: C.white, fontSize: '12px', marginBottom: '16px' }}>{error}</div>
          <button
            onClick={() => window.location.reload()}
            style={{ width: '100%', padding: '10px', backgroundColor: dynastyTheme.tokens.colors.primary, color: C.black, fontWeight: '600', fontSize: '12px', border: 'none', borderRadius: '6px' }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const currentPick = draftState?.currentPick;
  const picksMade = draftState?.picksMade || 0;
  const totalPicks = draftState?.totalPicks || 0;

  return (
    <div style={{ minHeight: '100vh', backgroundColor: 'transparent', paddingBottom: '140px' }}>
      {/* Header - compact */}
      <div style={{ backgroundColor: dynastyTheme.tokens.colors.neutral[800], padding: '10px 12px', borderBottom: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}` }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ color: C.white, fontWeight: '700', fontSize: '14px' }}>Live Draft</div>
            <div style={{ color: dynastyTheme.tokens.colors.neutral[400], fontSize: '10px' }}>{picksMade}/{totalPicks} picks</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {connected ? <Wifi size={14} color={dynastyTheme.tokens.colors.success} /> : <WifiOff size={14} color={dynastyTheme.tokens.colors.error} />}
            <span style={{ color: dynastyTheme.tokens.colors.neutral[400], fontSize: '10px' }}>{connected ? 'Live' : 'Offline'}</span>
          </div>
        </div>
      </div>

      {/* On The Clock Banner */}
      {currentPick && (
        <div style={{
          padding: '8px 12px',
          backgroundColor: isYourTurn ? C.primaryTint.t15 : dynastyTheme.tokens.colors.neutral[800],
          borderBottom: `1px solid ${isYourTurn ? dynastyTheme.tokens.colors.primary : dynastyTheme.tokens.colors.neutral[700]}`
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ color: dynastyTheme.tokens.colors.neutral[400], fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>On The Clock</div>
              <div style={{ color: isYourTurn ? dynastyTheme.tokens.colors.primary : C.white, fontWeight: '700', fontSize: '13px' }}>
                {isYourTurn ? 'YOUR PICK' : currentPick.current_owner?.team_name || 'Unknown'}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ color: dynastyTheme.tokens.colors.neutral[400], fontSize: '9px' }}>Pick #{currentPick.overall_pick_number}</div>
              <div style={{ color: dynastyTheme.tokens.colors.neutral[400], fontSize: '9px' }}>Rd {currentPick.round}</div>
            </div>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div style={{ display: 'flex', backgroundColor: dynastyTheme.tokens.colors.neutral[800], borderBottom: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}` }}>
        {[
          { id: 'board', label: 'Board' },
          { id: 'players', label: 'Players' },
          { id: 'roster', label: 'My Roster' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              flex: 1,
              padding: '8px 0',
              backgroundColor: 'transparent',
              border: 'none',
              borderBottom: activeTab === tab.id ? `2px solid ${dynastyTheme.tokens.colors.primary}` : '2px solid transparent',
              color: activeTab === tab.id ? dynastyTheme.tokens.colors.primary : dynastyTheme.tokens.colors.neutral[400],
              fontSize: '11px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div style={{ padding: '0' }}>
        {/* BOARD TAB */}
        {activeTab === 'board' && (
          <div style={{ padding: '8px' }}>
            {draftState?.picks?.slice().reverse().slice(0, 20).map((pick, idx) => (
              <div
                key={pick.pick_id || idx}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '6px 8px',
                  backgroundColor: pick.league_player_id ? dynastyTheme.tokens.colors.neutral[800] : 'transparent',
                  borderRadius: '4px',
                  marginBottom: '2px',
                  borderLeft: pick.current_owner_team_id === userTeam?.team_id ? `2px solid ${dynastyTheme.tokens.colors.primary}` : '2px solid transparent'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ color: dynastyTheme.tokens.colors.neutral[500], fontSize: '10px', width: '24px' }}>#{pick.overall_pick_number}</span>
                  <span style={{ color: dynastyTheme.tokens.colors.neutral[400], fontSize: '10px', width: '80px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {pick.current_owner?.team_name || 'Unknown'}
                  </span>
                </div>
                {pick.league_player_id ? (
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ color: C.white, fontSize: '11px', fontWeight: '600' }}>{pick.player?.info?.full_name}</div>
                    <div style={{ color: dynastyTheme.tokens.colors.neutral[400], fontSize: '9px' }}>{pick.player?.info?.position}</div>
                  </div>
                ) : (
                  <span style={{ color: dynastyTheme.tokens.colors.neutral[600], fontSize: '10px', fontStyle: 'italic' }}>—</span>
                )}
              </div>
            ))}
          </div>
        )}

        {/* PLAYERS TAB */}
        {activeTab === 'players' && (
          <div>
            {/* Filters */}
            <div style={{ padding: '8px 12px', display: 'flex', gap: '8px', borderBottom: `1px solid ${dynastyTheme.tokens.colors.neutral[800]}` }}>
              <select
                value={filters.position}
                onChange={(e) => setFilters(prev => ({ ...prev, position: e.target.value }))}
                style={{
                  flex: 1,
                  padding: '6px 8px',
                  backgroundColor: dynastyTheme.tokens.colors.neutral[800],
                  border: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
                  borderRadius: '4px',
                  color: C.white,
                  fontSize: '11px'
                }}
              >
                <option value="ALL">All</option>
                <option value="BATTERS">Batters</option>
                <option value="PITCHERS">Pitchers</option>
                <option value="C">C</option>
                <option value="1B">1B</option>
                <option value="2B">2B</option>
                <option value="SS">SS</option>
                <option value="3B">3B</option>
                <option value="OF">OF</option>
                <option value="SP">SP</option>
                <option value="RP">RP</option>
              </select>
              <input
                type="text"
                placeholder="Search..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                style={{
                  flex: 2,
                  padding: '6px 8px',
                  backgroundColor: dynastyTheme.tokens.colors.neutral[800],
                  border: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
                  borderRadius: '4px',
                  color: C.white,
                  fontSize: '11px'
                }}
              />
            </div>

            {/* Player List */}
            <MobileDynastyTable
              data={filteredPlayers}
              columns={playerColumns}
              tableType="flat-data"
              maxHeight="calc(100vh - 320px)"
              keyField="league_player_id"
            />
          </div>
        )}

        {/* ROSTER TAB */}
        {activeTab === 'roster' && (
          <div style={{ padding: '8px' }}>
            {userDraftedPlayers.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 20px', color: dynastyTheme.tokens.colors.neutral[500], fontSize: '12px' }}>
                No players drafted yet
              </div>
            ) : (
              <>
                {/* Active */}
                <SectionHeader title="Active" count={userDraftedPlayers.filter(p => p.roster_status === 'active').length} expanded={expandedSections.hitting} onToggle={() => toggleSection('hitting')} />
                {expandedSections.hitting && (
                  <div style={{ marginBottom: '12px' }}>
                    {userDraftedPlayers.filter(p => p.roster_status === 'active').map((pick, idx) => (
                      <RosterPlayerRow key={pick.league_player_id || idx} pick={pick} />
                    ))}
                  </div>
                )}

                {/* Bench */}
                <SectionHeader title="Bench" count={userDraftedPlayers.filter(p => p.roster_status === 'bench').length} expanded={expandedSections.bench} onToggle={() => toggleSection('bench')} />
                {expandedSections.bench && (
                  <div style={{ marginBottom: '12px' }}>
                    {userDraftedPlayers.filter(p => p.roster_status === 'bench').map((pick, idx) => (
                      <RosterPlayerRow key={pick.league_player_id || idx} pick={pick} />
                    ))}
                  </div>
                )}

                {/* Minors */}
                <SectionHeader title="Minors" count={userDraftedPlayers.filter(p => p.roster_status === 'minors').length} expanded={expandedSections.minors} onToggle={() => toggleSection('minors')} />
                {expandedSections.minors && (
                  <div style={{ marginBottom: '12px' }}>
                    {userDraftedPlayers.filter(p => p.roster_status === 'minors').map((pick, idx) => (
                      <RosterPlayerRow key={pick.league_player_id || idx} pick={pick} />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Selected Player + Draft Button - Fixed Bottom */}
      {selectedPlayer && isYourTurn && (
        <div style={{
          position: 'fixed',
          bottom: '65px',
          left: 0,
          right: 0,
          backgroundColor: dynastyTheme.tokens.colors.neutral[800],
          borderTop: `1px solid ${dynastyTheme.tokens.colors.primary}`,
          padding: '10px 12px',
          zIndex: 100
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div>
              <div style={{ color: C.white, fontWeight: '600', fontSize: '13px' }}>
                {selectedPlayer.info?.full_name}
              </div>
              <div style={{ color: dynastyTheme.tokens.colors.neutral[400], fontSize: '10px' }}>
                {selectedPlayer.info?.position} • {selectedPlayer.info?.mlb_team} • ${(selectedPlayer.financial?.baseline_price || 0).toFixed(0)}
              </div>
            </div>
            <button
              onClick={() => setSelectedPlayer(null)}
              style={{ padding: '4px 8px', backgroundColor: 'transparent', border: 'none', color: dynastyTheme.tokens.colors.neutral[400], fontSize: '11px', cursor: 'pointer' }}
            >
              Clear
            </button>
          </div>
          <button
            onClick={() => setShowPositionModal(true)}
            disabled={makingPick}
            style={{
              width: '100%',
              padding: '10px',
              backgroundColor: makingPick ? dynastyTheme.tokens.colors.neutral[700] : dynastyTheme.tokens.colors.primary,
              color: makingPick ? dynastyTheme.tokens.colors.neutral[500] : C.black,
              fontWeight: '700',
              fontSize: '12px',
              border: 'none',
              borderRadius: '6px',
              cursor: makingPick ? 'not-allowed' : 'pointer'
            }}
          >
            {makingPick ? 'Drafting...' : 'Draft to Position'}
          </button>
        </div>
      )}

      {/* Position Assignment Modal */}
      <MobilePositionAssignmentModal
        player={playerForAssignment}
        league={league}
        currentRoster={userRosterForAssignment}
        onAssign={handlePositionAssignment}
        onCancel={() => { setShowPositionModal(false); }}
        isVisible={showPositionModal}
        mode="add"
      />
    </div>
  );
};

// Section Header Component
const SectionHeader = ({ title, count, expanded, onToggle }) => (
  <button
    onClick={onToggle}
    style={{
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '8px 10px',
      backgroundColor: dynastyTheme.tokens.colors.neutral[800],
      border: 'none',
      borderRadius: '4px',
      marginBottom: '4px',
      cursor: 'pointer'
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <span style={{ color: C.white, fontWeight: '600', fontSize: '12px' }}>{title}</span>
      <span style={{ color: dynastyTheme.tokens.colors.neutral[500], fontSize: '10px' }}>({count})</span>
    </div>
    {expanded ? <ChevronUp size={14} color={dynastyTheme.tokens.colors.neutral[400]} /> : <ChevronDown size={14} color={dynastyTheme.tokens.colors.neutral[400]} />}
  </button>
);

// Roster Player Row Component
const RosterPlayerRow = ({ pick }) => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '6px 10px',
    backgroundColor: dynastyTheme.tokens.colors.neutral[850] || C.surface[845],
    borderRadius: '3px',
    marginBottom: '2px'
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      {pick.roster_position && (
        <span style={{
          backgroundColor: dynastyTheme.tokens.colors.primary,
          color: C.black,
          fontSize: '9px',
          fontWeight: '700',
          padding: '2px 4px',
          borderRadius: '2px',
          minWidth: '24px',
          textAlign: 'center'
        }}>
          {pick.roster_position.split('_')[0]}
        </span>
      )}
      <div>
        <div style={{ color: C.white, fontSize: '11px', fontWeight: '500' }}>{pick.player?.info?.full_name}</div>
        <div style={{ color: dynastyTheme.tokens.colors.neutral[500], fontSize: '9px' }}>{pick.player?.info?.position}</div>
      </div>
    </div>
    <span style={{ color: dynastyTheme.tokens.colors.success, fontSize: '10px', fontWeight: '600' }}>${(pick.salary || 0).toFixed(0)}</span>
  </div>
);

export default MobileLiveDraft;
